import React, { useState, useEffect } from "react";
import "./App.css";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import Rating from "./Rating";
import Ranking from "./Ranking";
import FilterDropdown from "./FilterDropdown";
import GoogleAdSense from "./GoogleAdSense";
import UserTier from "./UserTier";
import AllUserTiers from "./AllUserTiers";
import Year2026 from "./Year2026";
import VideoPage from "./VideoPage";
import SchoolPage from "./SchoolPage";
import { usePath, navigate, Link } from "./usePath";
import { updateEloRatings, pickProximityPair } from "./services";

const firebaseConfig = {
  apiKey: "AIzaSyBOQWB43fTjv_IIf4gfphtTfHOEx8mtNDI",
  authDomain: "callahan-rater.firebaseapp.com",
  projectId: "callahan-rater",
  storageBucket: "callahan-rater.firebasestorage.app",
  messagingSenderId: "687573579246",
  appId: "1:687573579246:web:4eb17307daa92fe77b4aff",
  measurementId: "G-HYSZXB8LJK",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

function App() {
  const path = usePath();
  const isYear2026 = path === "/2026";
  const isVideoPage = path.startsWith("/video/");
  const videoIdParam = isVideoPage ? path.slice("/video/".length) : null;
  const isSchoolPage = path.startsWith("/school/");
  const schoolParam = isSchoolPage
    ? decodeURIComponent(path.slice("/school/".length))
    : null;
  const hideFilters = isVideoPage || isSchoolPage;

  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [leftVideo, setLeftVideo] = useState(null);
  const [rightVideo, setRightVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageButton, setPageButton] = useState("Ranking");
  const [filters, setFilters] = useState({
    division: [],
    year: [],
    school: [],
    finalist: [],
    winner: [],
    award: [],
  });
  const [allVideos, setAllVideos] = useState([]);
  const [dailyVoteStats, setDailyVoteStats] = useState([]);

  const fetchAllVideos = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "videos"));
      const nomineesData = [];
      querySnapshot.forEach((doc) => {
        nomineesData.push({ id: doc.id, ...doc.data() });
      });
      setAllVideos(nomineesData);
      selectRandomVideos(nomineesData);
      setLoading(false);
      return nomineesData;
    } catch (error) {
      console.error("Error fetching nominees: ", error);
      setLoading(false);
    }
  };

  const fetchFilteredVideos = () => {
    setLoading(true);
    // Filter videos based on selected filters
    let filteredVideos = allVideos;

    // Apply each filter category
    Object.keys(filters).forEach((category) => {
      if (filters[category].length > 0) {
        if (category === "winner") {
          filteredVideos = filteredVideos.filter((video) => {
            if (filters[category].includes("Winner") && video.winner)
              return true;
            if (filters[category].includes("Finalist") && video.finalist)
              return true;
            if (
              filters[category].includes("Nominee") &&
              !video.winner &&
              !video.finalist
            )
              return true;
            return false;
          });
        } else if (category === "elo") {
          if (filters[category][0] !== "all") {
            filteredVideos = filteredVideos.filter((video) => {
              return video.elo >= filters[category];
            });
          }
        } else {
          filteredVideos = filteredVideos.filter((video) =>
            filters[category].includes(video[category])
          );
        }
      }
    });

    // If no videos match the filters, reset to all videos
    if (filteredVideos.length === 0) {
      filteredVideos = allVideos;
    }

    selectRandomVideos(filteredVideos);
    setLoading(false);
  };
  useEffect(() => {
    fetchAllVideos();
    fetchDailyVoteStats();
  }, []);

  useEffect(() => {
    if (allVideos.length > 0) {
      fetchFilteredVideos();
    }
  }, [filters, allVideos]);

  const selectRandomVideos = (videos) => {
    const [l, r] = pickProximityPair(videos);
    if (l && r) {
      setLeftVideo(l);
      setRightVideo(r);
    }
  };

  const handleVote = async (winnerId, loserId) => {
    setLoading(true);
    try {
      await updateEloRatings(winnerId, loserId, db);
      fetchFilteredVideos();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating Elo ratings:", error);
    }
    setLoading(false);
  };

  const handleFilterChange = (selectedFilters) => {
    setFilters(selectedFilters);
  };

  const fetchDailyVoteStats = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "dailyVotes"));
      const stats = [];
      querySnapshot.forEach((doc) => {
        stats.push({ id: doc.id, ...doc.data() });
      });
      stats.sort((a, b) => new Date(b.date) - new Date(a.date));
      setDailyVoteStats(stats);
    } catch (error) {
      console.error("Error fetching daily vote stats:", error);
    }
  };

  const switchPage = () => {
    setPageButton(pageButton === "Rating" ? "Ranking" : "Rating");
  };

  return (
    <div className="App">
      <FilterDropdown
        db={db}
        onFilterChange={handleFilterChange}
        switchPage={switchPage}
        pageButton={pageButton}
        isYear2026={isYear2026}
        onGoYear2026={() => navigate("/2026")}
        onGoHome={() => navigate("/")}
        homeOnly={hideFilters}
      />
      <header className="App-header">
        <h1>
          <Link to="/" style={{ color: "inherit", textDecoration: "none" }}>
            callahanvideos.com
          </Link>
        </h1>
      </header>
      {isVideoPage ? (
        <VideoPage
          videoId={videoIdParam}
          allVideos={allVideos}
          loading={loading}
        />
      ) : isSchoolPage ? (
        <SchoolPage
          schoolName={schoolParam}
          db={db}
          allVideos={allVideos}
          loading={loading}
          refreshVideos={fetchAllVideos}
          onVote={() => setRefreshTrigger((prev) => prev + 1)}
        />
      ) : isYear2026 ? (
        <Year2026
          db={db}
          allVideos={allVideos}
          refreshVideos={fetchAllVideos}
          onVote={() => setRefreshTrigger((prev) => prev + 1)}
        />
      ) : pageButton === "Ranking" ? (
        <Rating
          loading={loading}
          rightVideo={rightVideo}
          leftVideo={leftVideo}
          db={db}
          updateEloRatings={handleVote}
          fetchFilteredVideos={fetchFilteredVideos}
          dailyVoteStats={dailyVoteStats}
        />
      ) : (
        <Ranking db={db} filters={filters} />
      )}{" "}
      <GoogleAdSense />
      <br />
      <br />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <UserTier db={db} refreshTrigger={refreshTrigger} />
        <AllUserTiers db={db} refreshTrigger={refreshTrigger} />
      </div>
    </div>
  );
}

export default App;
